<template>
  <dialog-layout>
    <template v-slot:default>
      <!--Loading Indicator-->
      <div v-if="isLoading" class="text-center">
        <h1>Please wait...</h1>
        <loading></loading>
      </div>
      <!--/Loading Indicator-->

      <!--Import Confirmation-->
      <div v-else-if="isConfirmed" class="text-center">
        <h1>
          Import in Progress!
          <br>
          <small>This may take a few minutes.</small>
        </h1>
        <p>
          Our servers are importing your {{ confirmImportCount }} contacts and researching their giving histories.
          This is happening in the background, so you do not need to stay on this page. We will send you an email
          when the import is complete.
        </p>
        <router-link :to="{ name: 'contacts' }" class="button">Search Contacts</router-link>
      </div>
      <!--/Import Confirmation-->

      <!--Import Buttons-->
      <div v-else class="text-center">
        <h1 class="margin-bottom">
          Import Contacts
          <br>
          <small>Choose a source to import a list of your contacts</small>
        </h1>

        <import-buttons @data="handleData($event)"></import-buttons>
      </div>
    </template>

    <template v-slot:footer>
      <div class="grid-x">
        <div class="cell medium-10 medium-offset-1 large-8 large-offset-2">
          <div class="text-center">
            <contacts-remaining-progress-bar />
          </div>
        </div>
      </div>
    </template>

  </dialog-layout>
</template>

<script>
import DialogLayout from '../../layouts/DialogLayout.vue'
import ImportsService from '../../services/ImportsService'
import ImportButtons from '../../components/Import/ImportButtons.vue'
import CampaignService from '../../services/CampaignService'
import Loading from '../../components/Common/Loading.vue'
import ContactsRemainingProgressBar from '../../components/Import/ContactsRemainingProgressBar'

export default {
  components: {
    ContactsRemainingProgressBar,
    DialogLayout,
    ImportButtons,
    Loading
  },
  data () {
    return {
      confirmImportCount: null,
      isLoading: false
    }
  },
  computed: {
    isConfirmed () {
      return this.confirmImportCount !== null
    }
  },
  methods: {
    async handleData (data) {
      // hide the confirmation from any past imports
      this.hideConfirmation()
      // be sure that the data is formatted correctly
      if (!data.source || !data.hasOwnProperty('sourceMeta') || !Array.isArray(data.contacts)) {
        throw new Error('The contact importer returned data in an invalid format')
      }
      // if there was no data, then don't do anything
      if (data.contacts.length === 0) {
        return
      }

      // can this campaign upload this number of contacts?
      const remainingContacts = await this.getCampaignRemainingContactCount()
      if (remainingContacts !== null && remainingContacts < data.contacts.length) {
        let message = `You are trying to upload ${data.contacts.length} contacts, `
        if (remainingContacts > 0) {
          message += `but your account may only upload ${remainingContacts} contacts at this time.`
        } else {
          message += `but your account has reached its database size limit.`
        }
        message += `\n\nPlease contact help@calltime.ai if you'd like to upload more contacts.`
        alert(message)
        return
      }

      // run the import
      this.isLoading = true
      await this.importContacts(data.source, data.sourceMeta, data.contacts)
      this.isLoading = false
      // show confirmation
      this.showConfirmation(data)
    },
    async importContacts (source, sourceMeta, contacts) {
      return ImportsService.importContacts(source, sourceMeta, contacts)
    },
    hideConfirmation () {
      this.confirmImportCount = null
    },
    showConfirmation (data) {
      this.confirmImportCount = data.contacts.length
    },
    /**
     * How many records does this campaign have left to upload?
     * @return {Promise<null|Integer>}
     */
    async getCampaignRemainingContactCount () {
      const remaining = await CampaignService.getRecordsRemainingCount()
      return remaining.remaining
    }
  }
}
</script>
